/**
 * Notes Manager
 * Factory function for managing user notes with CRUD operations and search
 *
 * IMPORTANT: This module does NOT directly depend on platform.
 * Storage implementation and searcher must be injected via options.
 */

import { generateMetadata } from './auto-metadata.js'
import { NotesStorage } from './storage.js'
import { getUserId } from 'workers-common'
import {
  MAX_CONTENT_LENGTH,
  MAX_NOTES_PER_USER,
} from './constants.js'

/**
 * Convert note to searchable text
 */
function noteToText(note) {
  const title = note.title || ''
  const content = note.content || ''
  return `${title}\n${content}`.trim()
}

/**
 * Generate a unique note ID
 */
function generateNoteId() {
  const timestamp = Date.now()
  const random = Math.floor(Math.random() * 1000)
  return `note_${timestamp}_${random}`
}

/**
 * Validate note content
 */
function validateContent(content) {
  if (!content || typeof content !== 'string') {
    throw new Error('Content is required and must be a string')
  }
  if (content.length > MAX_CONTENT_LENGTH) {
    throw new Error(`Content exceeds maximum length of ${MAX_CONTENT_LENGTH} characters`)
  }
  if (content.trim().length === 0) {
    throw new Error('Content cannot be empty')
  }
}

/**
 * Create Notes Manager API
 * Returns an object with note operations that can be used directly in sandbox
 *
 * @param {Object} options - { store, apiKeys, llmSdk, searcher }
 * @param {Object} options.store - Storage implementation (required)
 * @param {Object} options.searcher - Semantic searcher (required): { search: Function }
 * @param {Object} options.apiKeys - API keys for LLM providers
 * @param {Object} options.llmSdk - LLM SDK instance
 * @returns {Object} API functions for sandbox use
 */
export function createNotesManager(options) {
  if (!options.store) {
    throw new Error('Store implementation is required')
  }
  if (!options.searcher) {
    throw new Error('searcher is required')
  }

  const storage = new NotesStorage(options.store)
  const apiKeys = options.apiKeys || {}
  const llmSdk = options.llmSdk || null
  const searcher = options.searcher

  // Per-user write serialization to prevent read-modify-write races
  const locks = new Map()
  const withLock = (userId, fn) => {
    const prev = locks.get(userId) || Promise.resolve()
    const next = prev.then(fn)
    locks.set(userId, next.catch(() => {}))
    return next
  }

  const requireAuth = (ctx) => {
    const userId = getUserId(ctx)
    if (!userId) {
      throw new Error('User not authenticated')
    }
    return userId
  }

  // Internal operations (work with userId directly)
  const saveNoteInternal = async (userId, noteData) => {
    const { content, title, tags, metadata = {} } = noteData

    validateContent(content)

    const existingNotes = await storage.loadNotes(userId)
    if (existingNotes.length >= MAX_NOTES_PER_USER) {
      throw new Error(`User has reached maximum of ${MAX_NOTES_PER_USER} notes`)
    }

    const noteId = generateNoteId()

    let finalTitle = title
    let finalTags = tags

    if (!finalTitle || !finalTags) {
      console.log('[NotesManager] Auto-generating metadata...')
      const provider = apiKeys.gemini?.apiKey ? 'gemini' : apiKeys.openai?.apiKey ? 'openai' : null
      const apiKey = provider ? (apiKeys[provider]?.apiKey || apiKeys[provider]) : null
      const generated = await generateMetadata(content, {
        chat: llmSdk?.chat,
        provider,
        apiKey,
      })
      finalTitle = finalTitle || generated.title
      finalTags = finalTags || generated.tags
    }

    const note = {
      id: noteId,
      userId,
      title: finalTitle,
      content,
      tags: Array.isArray(finalTags) ? finalTags : [],
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      metadata: { ...metadata, source: metadata.source || 'api' },
    }

    await storage.addNote(userId, note)
    console.log(`[NotesManager] Saved note ${noteId} for user ${userId.substring(0, 8)}...`)
    return note
  }

  const searchNotesInternal = async (userId, query, options = {}) => {
    const { limit = 5 } = options
    const allNotes = await storage.loadNotes(userId)

    if (allNotes.length === 0) return []

    const strings = allNotes.map(noteToText)
    const results = await searcher.search(query, strings, { limit })

    const noteResults = results.map((r) => allNotes[r.index])
    console.log(`[NotesManager] Search for "${query}" returned ${noteResults.length} results for user ${userId.substring(0, 8)}...`)
    return noteResults
  }

  const listNotesInternal = async (userId, options = {}) => {
    const { offset = 0, limit = 50, sortBy = 'createdAt', order = 'desc' } = options
    let notes = await storage.loadNotes(userId)

    notes.sort((a, b) => {
      const aVal = a[sortBy]
      const bVal = b[sortBy]

      if (sortBy === 'createdAt' || sortBy === 'updatedAt') {
        return order === 'desc' ? new Date(bVal) - new Date(aVal) : new Date(aVal) - new Date(bVal)
      }

      if (aVal < bVal) return order === 'desc' ? 1 : -1
      if (aVal > bVal) return order === 'desc' ? -1 : 1
      return 0
    })

    const paginated = notes.slice(offset, offset + limit)
    console.log(`[NotesManager] Listed ${paginated.length} notes for user ${userId.substring(0, 8)}...`)
    return paginated
  }

  const getNoteByIdInternal = async (userId, noteId) => {
    const note = await storage.getNote(userId, noteId)
    if (note && note.userId !== userId) {
      throw new Error('Permission denied')
    }
    return note
  }

  const updateNoteInternal = async (userId, noteId, updates) => {
    const existing = await getNoteByIdInternal(userId, noteId)
    if (!existing) {
      throw new Error('Note not found')
    }

    if (updates.content !== undefined) {
      validateContent(updates.content)
    }

    const allowedUpdates = {}
    if (updates.content !== undefined) allowedUpdates.content = updates.content
    if (updates.title !== undefined) allowedUpdates.title = updates.title
    if (updates.tags !== undefined) {
      allowedUpdates.tags = Array.isArray(updates.tags) ? updates.tags : []
    }

    const updated = await storage.updateNote(userId, noteId, allowedUpdates)
    console.log(`[NotesManager] Updated note ${noteId} for user ${userId.substring(0, 8)}...`)
    return updated
  }

  const deleteNoteInternal = async (userId, noteId) => {
    const existing = await getNoteByIdInternal(userId, noteId)
    if (!existing) {
      throw new Error('Note not found')
    }

    const success = await storage.deleteNote(userId, noteId)
    if (success) {
      console.log(`[NotesManager] Deleted note ${noteId} for user ${userId.substring(0, 8)}...`)
    }
    return success
  }

  const getStatsInternal = async (userId) => {
    return await storage.getStats(userId)
  }

  // Public API (for sandbox use with ctx)
  const api = {
    save: async (ctx, params) => {
      const userId = requireAuth(ctx)
      return withLock(userId, async () => {
        const { content, title, tags, metadata } = params
        const note = await saveNoteInternal(userId, { content, title, tags, metadata })
        return { success: true, note }
      })
    },

    search: async (ctx, params) => {
      const userId = requireAuth(ctx)
      const { query, limit = 5 } = params
      if (!query || typeof query !== 'string' || query.trim().length === 0) {
        throw new Error('query is required and must be a non-empty string')
      }
      if (query.length > 1000) {
        throw new Error('query too long (max 1000 characters)')
      }
      const notes = await searchNotesInternal(userId, query, { limit })
      return { success: true, notes }
    },

    list: async (ctx, params) => {
      const userId = requireAuth(ctx)
      const { limit = 10, offset = 0, sortBy = 'createdAt' } = params
      const result = await listNotesInternal(userId, { limit, offset, sortBy })
      return { success: true, notes: result, total: result.length }
    },

    getById: async (ctx, params) => {
      const userId = requireAuth(ctx)
      const { noteId } = params
      const note = await getNoteByIdInternal(userId, noteId)
      if (!note) {
        throw new Error(`Note ${noteId} not found`)
      }
      return { success: true, note }
    },

    update: async (ctx, params) => {
      const userId = requireAuth(ctx)
      return withLock(userId, async () => {
        const { noteId, content, title, tags } = params
        const note = await updateNoteInternal(userId, noteId, { content, title, tags })
        return { success: true, note }
      })
    },

    updateByQuery: async (ctx, params) => {
      const userId = requireAuth(ctx)
      return withLock(userId, async () => {
        const { query, newContent } = params
        if (!query || !newContent) {
          throw new Error('query and newContent are required')
        }
        const notes = await searchNotesInternal(userId, query, { limit: 1 })
        if (!notes || notes.length === 0) {
          throw new Error('No matching note found')
        }
        const note = await updateNoteInternal(userId, notes[0].id, { content: newContent })
        return { success: true, note }
      })
    },

    delete: async (ctx, params) => {
      const userId = requireAuth(ctx)
      return withLock(userId, async () => {
        const { noteId } = params
        await deleteNoteInternal(userId, noteId)
        return { success: true, message: `Note ${noteId} deleted` }
      })
    },

    getStats: async (ctx) => {
      const userId = requireAuth(ctx)
      const stats = await getStatsInternal(userId)
      return { success: true, stats }
    },
  }

  Object.defineProperty(api, 'sandbox', { value: { ...api }, enumerable: false })
  return api
}
